// HistoryInspection — the one surface: two modes, three lenses, drill-down.
//
// The synthesis verdict (history-inspection-synthesis.md §2): NOT three
// separate destinations but ONE surface with graduated doors, because the
// links between the lenses are navigation, not see-also. Everything traces to
// the one event log; the author holds ONE chunk ("the place I see the past and
// what the agent knows"), not three.
//
// Two top-level modes answer the two questions:
//   • Timeline — "what happened" (the everyday browse).
//   • What the agent knows — "what does it know / ground on" (the power audit,
//     three sub-lenses). The wedge is honoured by door-sizing upstream, not by
//     splitting the surface.
//
// Drill-down is the spine, modelled as a view sum (never leave the surface,
// descend through it):
//   • a timeline `edited_material` row expands into the per-resource
//     file-history lens (S2/S3);
//   • an `asserted_fact` row doors into the Knowledge-bank lens.
//
// Read-only throughout, save the one restore — which this surface does NOT
// dispatch (it calls the `onRestore` prop; the integrator wires the command).
// Pure/props: all data arrives resolved; the only state is which view is open.

export type { HistoryInspectionProps };
export { HistoryInspection };

import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useState } from "react";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { SegmentedControl } from "../SegmentedControl/SegmentedControl.js";
import { Spinner } from "../Spinner/Spinner.js";
import type { FileHistoryLensProps } from "./FileHistory/FileHistoryLens.js";
import { FileHistoryLens } from "./FileHistory/FileHistoryLens.js";
import {
    InspectionLens,
    type InspectionLensProps,
    type InspectionSubLens,
} from "./Inspection/InspectionLens.js";
import { TimelineLens } from "./Timeline/TimelineLens.js";
import type { TimelineRow, TimelineSection } from "./types.js";
import "./HistoryInspection.css";

// The surface's view-state — a sum, so an illegal combination (in file-history
// with no resource) can't be built.
type SurfaceView =
    | { readonly type: "timeline" }
    | { readonly type: "inspection" }
    | { readonly type: "file_history"; readonly resourceKey: string };

type HistoryInspectionProps = {
    // Timeline — pre-folded, resolved, date-sectioned.
    readonly sections: readonly TimelineSection[];
    readonly onReveal?: (row: TimelineRow) => void;

    // Inspection — the three lenses' data + handlers (minus the initial sub,
    // which the surface drives to route a fact-row door into Knowledge-bank).
    readonly inspection: Omit<InspectionLensProps, "initial">;

    // File-history — the per-resource view for the currently-drilled resource,
    // or undefined until the integrator loads it (asked via onRequestFileHistory).
    readonly fileHistory?: FileHistoryLensProps;
    readonly onRequestFileHistory?: (resourceKey: string) => void;
};

const HistoryInspection = ({
    sections,
    onReveal,
    inspection,
    fileHistory,
    onRequestFileHistory,
}: HistoryInspectionProps): ReactNode => {
    const { t } = useLingui();
    const [view, setView] = useState<SurfaceView>({ type: "timeline" });
    // The sub-lens Inspection opens at — bumped (via key) when a fact-row door
    // requests Knowledge-bank, so the internal sub-state resets to it.
    const [inspectionInitial, setInspectionInitial] =
        useState<InspectionSubLens>("knows_you");

    // Both timeline and its file-history drill-down live under the "timeline"
    // mode; only inspection flips the top-level toggle.
    const mode = view.type === "inspection" ? "inspection" : "timeline";

    const openTimeline = () => setView({ type: "timeline" });
    const openInspection = () => setView({ type: "inspection" });

    const expandResource = (row: TimelineRow) => {
        if (row.resourceKey === undefined) return;
        onRequestFileHistory?.(row.resourceKey);
        setView({ type: "file_history", resourceKey: row.resourceKey });
    };
    const openKnowledge = () => {
        setInspectionInitial("knowledge_bank");
        setView({ type: "inspection" });
    };

    const modeOptions = [
        { value: "timeline", label: t`Timeline` },
        { value: "inspection", label: t`What the agent knows` },
    ];

    return (
        <div className="bilda-history-inspection">
            <div className="bilda-history-inspection__header">
                <SegmentedControl
                    options={modeOptions}
                    value={mode}
                    onChange={(value) =>
                        value === "inspection"
                            ? openInspection()
                            : openTimeline()
                    }
                    ariaLabel={t`Choose a mode`}
                    size="md"
                />
                {view.type === "file_history" && (
                    <Button variant="ghost" size="sm" onClick={openTimeline}>
                        <Icon name="chevron-left" size="xs" />
                        <Trans>Back to timeline</Trans>
                    </Button>
                )}
            </div>

            <div className="bilda-history-inspection__body">
                {view.type === "timeline" && (
                    <TimelineLens
                        sections={sections}
                        onReveal={onReveal}
                        onExpandResource={expandResource}
                        onOpenKnowledge={openKnowledge}
                    />
                )}

                {view.type === "inspection" && (
                    <InspectionLens
                        key={inspectionInitial}
                        initial={inspectionInitial}
                        {...inspection}
                    />
                )}

                {view.type === "file_history" &&
                    (fileHistory !== undefined ? (
                        <FileHistoryLens {...fileHistory} />
                    ) : (
                        <div className="bilda-history-inspection__loading">
                            <Spinner label={t`Loading version history`} />
                            <Trans>Loading version history…</Trans>
                        </div>
                    ))}
            </div>
        </div>
    );
};
